// =====================================================
// CompareContext — Global state for scholarship comparison
// Allows students to select up to 4 scholarships from
// anywhere in the app and compare them side-by-side.
// =====================================================

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { Scholarship } from '@/types';
import { getScholarshipById } from '@/data/scholarships';

const MAX_COMPARE = 4;
const COMPARE_STORAGE_KEY = 'sv-compare-list';

interface CompareContextValue {
  /** Scholarships currently selected for comparison */
  compareList: Scholarship[];
  /** Add a scholarship to the compare list */
  addToCompare: (scholarship: Scholarship) => boolean;
  /** Remove a scholarship from the compare list */
  removeFromCompare: (id: string) => void;
  /** Check if a scholarship is currently in the compare list */
  isInCompare: (id: string) => boolean;
  /** Toggle a scholarship in/out of the compare list */
  toggleCompare: (scholarship: Scholarship) => void;
  /** Clear the entire compare list */
  clearCompare: () => void;
  /** Whether the compare drawer is open */
  isDrawerOpen: boolean;
  /** Open the compare drawer */
  openDrawer: () => void;
  /** Close the compare drawer */
  closeDrawer: () => void;
  /** Max allowed items */
  maxCompare: number;
}

const CompareContext = createContext<CompareContextValue | undefined>(undefined);

export function CompareProvider({ children }: { children: ReactNode }) {
  const [compareList, setCompareList] = useState<Scholarship[]>(() => {
    try {
      const stored = localStorage.getItem(COMPARE_STORAGE_KEY);
      if (stored) {
        const ids: string[] = JSON.parse(stored);
        return ids.map(getScholarshipById).filter(Boolean) as Scholarship[];
      }
    } catch { /* ignore */ }
    return [];
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Persist compare list IDs to localStorage
  useEffect(() => {
    try {
      const ids = compareList.map(s => s.id);
      localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(ids));
    } catch { /* localStorage full — ignore */ }
  }, [compareList]);

  const addToCompare = useCallback((scholarship: Scholarship): boolean => {
    let added = false;
    setCompareList(prev => {
      if (prev.length >= MAX_COMPARE) return prev;
      if (prev.some(s => s.id === scholarship.id)) return prev;
      added = true;
      return [...prev, scholarship];
    });
    return added;
  }, []);

  const removeFromCompare = useCallback((id: string) => {
    setCompareList(prev => prev.filter(s => s.id !== id));
  }, []);

  const isInCompare = useCallback((id: string) => {
    return compareList.some(s => s.id === id);
  }, [compareList]);

  const toggleCompare = useCallback((scholarship: Scholarship) => {
    setCompareList(prev => {
      if (prev.some(s => s.id === scholarship.id)) {
        return prev.filter(s => s.id !== scholarship.id);
      }
      if (prev.length >= MAX_COMPARE) return prev;
      return [...prev, scholarship];
    });
  }, []);

  const clearCompare = useCallback(() => {
    setCompareList([]);
  }, []);

  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  return (
    <CompareContext.Provider
      value={{
        compareList,
        addToCompare,
        removeFromCompare,
        isInCompare,
        toggleCompare,
        clearCompare,
        isDrawerOpen,
        openDrawer,
        closeDrawer,
        maxCompare: MAX_COMPARE,
      }}
    >
      {children}
    </CompareContext.Provider>
  );
}

// Safe no-op defaults so consumers don't crash if rendered outside CompareProvider
const COMPARE_FALLBACK: CompareContextValue = {
  compareList: [],
  addToCompare: () => false,
  removeFromCompare: () => {},
  isInCompare: () => false,
  toggleCompare: () => {},
  clearCompare: () => {},
  isDrawerOpen: false,
  openDrawer: () => {},
  closeDrawer: () => {},
  maxCompare: MAX_COMPARE,
};

export function useCompare() {
  const context = useContext(CompareContext);
  if (!context) {
    console.warn('[useCompare] called outside CompareProvider — returning safe defaults');
    return COMPARE_FALLBACK;
  }
  return context;
}
